// PalWorldSettings.ini model: parse, validate and serialise. Client-safe (no data imports at module level besides the settings table).
import { PAL_SETTINGS_DATA } from "./pal-server-settings-data";

export type PalSetting = {
  key: string;
  kind: "text" | "password" | "number" | "bool" | "enum" | "platforms" | "raw";
  label: string;
  help: string;
  default: string | number | boolean | string[] | null;
  min?: number | null;
  max?: number | null;
  step?: number;
  options?: [string, string][];
};
export type PalSection = { title: string; optional?: boolean; settings: PalSetting[] };
export type PalSettingsData = { order: string[]; sections: PalSection[] };

export const SECTIONS = PAL_SETTINGS_DATA.sections;
export const ALL = SECTIONS.flatMap((s) => s.settings);
const BY_KEY = new Map(ALL.map((s) => [s.key, s]));
export const PLATFORMS = ["Steam", "Xbox", "PS5", "Mac"];

/** Value of one setting. `undefined` = not written (server default). Unknown keys keep their raw text. */
export type Values = Record<string, string | number | boolean | string[] | undefined>;

export function defaults(): Values {
  const v: Values = {};
  for (const s of ALL) if (s.default !== null) v[s.key] = Array.isArray(s.default) ? [...s.default] : s.default;
  return v;
}

const isFloat = (s: PalSetting) => (s.step ?? 1) < 1;
function fmtNumber(s: PalSetting | undefined, n: number) {
  if (!Number.isFinite(n)) return "0";
  return s && isFloat(s) ? n.toFixed(6) : String(Math.round(n));
}
const clean = (v: string) => v.replace(/["\r\n]/g, "");

function fmt(key: string, v: Values[string]): string | null {
  if (v === undefined) return null;
  const s = BY_KEY.get(key);
  if (!s) return String(v); // unknown key: raw text as imported
  switch (s.kind) {
    case "bool":
      return v ? "True" : "False";
    case "number":
      return fmtNumber(s, Number(v));
    case "enum":
      return String(v);
    case "platforms":
      return `(${(Array.isArray(v) ? v : []).join(",")})`;
    case "raw":
      return String(v).trim() || null;
    default:
      return `"${clean(String(v))}"`;
  }
}

/** Serialise in the official key order, then the 1.0 additions, then any unknown keys from an imported file. */
export function toIni(values: Values) {
  const keys = [...PAL_SETTINGS_DATA.order, ...ALL.map((s) => s.key).filter((k) => !PAL_SETTINGS_DATA.order.includes(k))];
  for (const k of Object.keys(values)) if (!keys.includes(k)) keys.push(k);
  const parts: string[] = [];
  for (const k of keys) {
    const f = fmt(k, values[k]);
    if (f !== null) parts.push(`${k}=${f}`);
  }
  return `[/Script/Pal.PalGameWorldSettings]\nOptionSettings=(${parts.join(",")})\n`;
}

/** Split "A=1,B="x,y",C=(Steam,Xbox)" respecting quotes and parentheses. */
function splitPairs(body: string) {
  const out: [string, string][] = [];
  let cur = "";
  let q = false;
  let depth = 0;
  const push = () => {
    const i = cur.indexOf("=");
    if (i > 0) out.push([cur.slice(0, i).trim(), cur.slice(i + 1).trim()]);
    cur = "";
  };
  for (const c of body) {
    if (c === '"') q = !q;
    if (!q && c === "(") depth++;
    if (!q && c === ")") depth--;
    if (c === "," && !q && depth === 0) push();
    else cur += c;
  }
  if (cur.trim()) push();
  return out;
}

export function fromIni(text: string): { values: Values; notes: string[] } {
  const start = text.indexOf("OptionSettings=(");
  if (start === -1) throw new Error("Couldn't find OptionSettings=( … ) in that file.");
  const open = start + "OptionSettings=(".length;
  // find the matching closing paren
  let depth = 1;
  let q = false;
  let end = open;
  for (; end < text.length; end++) {
    const c = text[end];
    if (c === '"') q = !q;
    if (q) continue;
    if (c === "(") depth++;
    if (c === ")" && --depth === 0) break;
  }
  const values = defaults();
  const notes: string[] = [];
  const unknown: string[] = [];
  let read = 0;
  for (const [k, raw] of splitPairs(text.slice(open, end))) {
    read++;
    const s = BY_KEY.get(k);
    if (!s) {
      values[k] = raw;
      unknown.push(k);
      continue;
    }
    const unq = raw.replace(/^"|"$/g, "");
    if (s.kind === "bool") values[k] = /^true$/i.test(unq);
    else if (s.kind === "number") values[k] = Number(unq);
    else if (s.kind === "platforms") values[k] = unq.replace(/[()\s]/g, "").split(",").filter(Boolean);
    else if (s.kind === "raw") values[k] = raw;
    else values[k] = unq;
  }
  notes.push(`Read ${read} settings from your file.`);
  if (unknown.length) notes.push(`Kept ${unknown.length} setting${unknown.length > 1 ? "s" : ""} this tool doesn't know yet, unchanged: ${unknown.join(", ")}.`);
  return { values, notes };
}

export type Issue = { level: "error" | "warn" | "info"; text: string };

export function check(v: Values): Issue[] {
  const out: Issue[] = [];
  for (const s of ALL) {
    const x = v[s.key];
    if (x === undefined) continue;
    if (s.kind === "number") {
      const n = Number(x);
      if (!Number.isFinite(n)) out.push({ level: "error", text: `${s.key} isn't a number.` });
      else if ((s.min != null && n < s.min) || (s.max != null && n > s.max)) out.push({ level: "warn", text: `${s.key} = ${n} is outside the usual ${s.min ?? "…"}–${s.max ?? "…"} range.` });
    }
    if (s.kind === "enum" && s.options && !s.options.some(([o]) => o === x)) out.push({ level: "error", text: `${s.key}: "${String(x)}" isn't a valid option.` });
  }
  if (!String(v.AdminPassword ?? "")) out.push({ level: "warn", text: "No admin password: nobody can use admin commands." });
  if (v.RCONEnabled && !String(v.AdminPassword ?? "")) out.push({ level: "error", text: "RCON is on but the admin password is empty. RCON logs in with the admin password." });
  if (v.RESTAPIEnabled && !String(v.AdminPassword ?? "")) out.push({ level: "error", text: "The REST API is on but the admin password is empty." });
  if (!String(v.ServerPassword ?? "")) out.push({ level: "info", text: "No server password, so anyone can join." });
  const plats = v.CrossplayPlatforms;
  if (Array.isArray(plats) && plats.length === 0) out.push({ level: "error", text: "No crossplay platform selected, so nobody can connect." });
  if (v.bHardcore && v.DeathPenalty === "None") out.push({ level: "info", text: "Hardcore is on, so the death penalty barely matters: players can't respawn." });
  return out;
}
